import { useTranslation } from "react-i18next";
import { CheckIcon, CaretRight } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { PricingRegionChip } from "@/components/pricing/PricingRegionChip";
import type { ServiceInfo } from "./types";

function modalityChip(modality: ServiceInfo["modality"], t: (k: string) => string): string {
  if (modality === "online") return t("booking.modalityOnline");
  if (modality === "in_person") return t("booking.modalityInPerson");
  return `${t("booking.modalityOnline")} · ${t("booking.modalityInPerson")}`;
}

export function ServicePicker({
  services,
  resolvedCountry,
  selectedId,
  viewerRole,
  onSelect,
}: {
  services: ServiceInfo[];
  resolvedCountry?: string | null;
  selectedId?: string | null;
  viewerRole?: string | null;
  onSelect: (service: ServiceInfo) => void;
}) {
  const { t } = useTranslation();
  const canChangePricingCountry = viewerRole === "admin";

  if (services.length === 0) {
    return (
      <p className="rounded-xl bg-muted/20 py-6 text-center text-sm text-muted-foreground">
        {t("booking.noServices")}
      </p>
    );
  }

  return (
    <div>
      {resolvedCountry !== undefined && canChangePricingCountry && (
        <div className="mb-3">
          <PricingRegionChip resolvedCountry={resolvedCountry ?? null} />
        </div>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        {services.map((s, index) => {
          const active = selectedId === s.id;
          const spotlight = index === 0 && !selectedId;
          return (
            <button
              key={s.id}
              data-testid={`booking-service-${s.id}`}
              onClick={() => onSelect(s)}
              className={cn(
                "group relative flex items-stretch overflow-hidden rounded-2xl border bg-card text-left transition-all",
                active
                  ? "border-primary shadow-[0_8px_24px_-14px_rgba(35,25,10,0.35)] ring-1 ring-primary"
                  : "border-border/70 hover:-translate-y-0.5 hover:border-primary/60 hover:shadow-[0_10px_28px_-16px_rgba(35,25,10,0.3)]",
                spotlight && "animate-booking-spotlight",
              )}
            >
              {/* accent rail */}
              <span
                aria-hidden
                className={cn(
                  "w-1 shrink-0 transition-colors",
                  active ? "bg-primary" : "bg-transparent group-hover:bg-primary/40",
                )}
              />

              <div className="flex min-w-0 flex-1 flex-col gap-3 p-4 sm:p-5">
                <div>
                  <p className="text-[17px] font-semibold leading-snug text-foreground" style={{ fontFamily: "var(--font-heading)" }}>
                    {s.name}
                  </p>
                  {s.description && (
                    <p className="mt-1 text-[13px] leading-relaxed whitespace-pre-line text-muted-foreground">{s.description}</p>
                  )}
                </div>
                <div className="mt-auto flex flex-wrap items-center gap-2">
                  <span className="rounded-full border border-border bg-muted/40 px-2.5 py-0.5 text-[11.5px] text-muted-foreground">
                    {s.durationMinutes} min
                  </span>
                  <span className="rounded-full border border-border bg-muted/40 px-2.5 py-0.5 text-[11.5px] text-muted-foreground">
                    {modalityChip(s.modality, t)}
                  </span>
                </div>
              </div>

              {/* price panel */}
              <div
                className={cn(
                  "flex shrink-0 flex-col items-end justify-center gap-1.5 border-l px-4 py-4 transition-colors sm:px-5",
                  active ? "border-primary/30 bg-primary/[0.06]" : "border-border/60 group-hover:bg-muted/30",
                )}
              >
                <span className="whitespace-nowrap text-xl font-bold text-foreground sm:text-2xl" style={{ fontFamily: "var(--font-heading)" }}>
                  {s.price} KM
                </span>
                {active ? (
                  <span className="flex items-center gap-1 text-xs font-semibold text-primary">
                    <span className="grid size-4 place-items-center rounded-full bg-primary">
                      <CheckIcon className="size-2.5 text-background" weight="bold" />
                    </span>
                  </span>
                ) : (
                  <span className="flex items-center gap-0.5 whitespace-nowrap text-xs font-semibold text-primary opacity-0 transition-opacity group-hover:opacity-100">
                    {t("booking.selectCta")} <CaretRight className="size-3.5" weight="bold" />
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
